import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { kg, todayCL } from "../lib/time";
import { PageTitle } from "../components/Common";
import { ScopeFilters } from "./Bins";
import { SetupChecklist } from "../components/SetupChecklist";
import { PendingList } from "./Dispatch";
import { useAuth } from "../lib/auth";

const Kpi = ({ label, value, sub, testid, tone }) => (
  <div className={`card p-4 ${tone || ""}`} data-testid={testid}>
    <p className="eyebrow">{label}</p>
    <p className="font-display text-3xl font-extrabold mt-1">{value}</p>
    {sub && <p className="text-xs font-semibold text-slate-600 mt-1">{sub}</p>}
  </div>
);

const KpiRow = ({ d, target, t, prefix }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
    <Kpi label={t("kpi_activated")} value={d.activated} testid={`${prefix}-kpi-activated`} />
    <Kpi label={t("kpi_ready")} value={d.ready} testid={`${prefix}-kpi-ready`} />
    <Kpi label={t("kpi_received")} value={d.received} testid={`${prefix}-kpi-received`} />
    <Kpi label={t("kpi_verified_kg")} value={kg(d.verified_kg)} sub={`${d.verified} ${t("bins")}`} testid={`${prefix}-kpi-verified-kg`} />
    <Kpi label={t("kpi_avg")} value={kg(d.avg_net_kg)} testid={`${prefix}-kpi-avg`}
      sub={d.avg_vs_target_kg !== null ? `${d.avg_vs_target_kg > 0 ? "+" : ""}${d.avg_vs_target_kg} kg ${t("vs_target")} ${target}` : `${t("target")} ${target} kg`} />
  </div>
);

const Breakdown = ({ title, rows, t, testid }) => (
  <div className="card overflow-hidden" data-testid={testid}>
    <h3 className="font-bold px-4 py-3 border-b">{title}</h3>
    <table className="tbl w-full"><thead><tr><th>{t("name")}</th><th>{t("bins")}</th><th>{t("verified_kg")}</th><th>{t("avg")}</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.id || "x"}><td className="font-semibold">{r.name}</td><td className="font-mono">{r.bins}</td><td className="font-mono">{r.verified_kg}</td><td className="font-mono">{r.avg_net_kg ?? "—"}</td></tr>)}
        {!rows.length && <tr><td colSpan={4} className="text-slate-500">{t("none")}</td></tr>}</tbody></table>
  </div>
);

const Productivity = ({ title, rows, t, testid }) => (
  <div className="card overflow-x-auto" data-testid={testid}>
    <h3 className="font-bold px-4 py-3 border-b">{title}</h3>
    <table className="tbl w-full"><thead><tr><th>{t("name")}</th><th>{t("pickers")}</th><th>{t("picker_hours")}</th><th>{t("verified_kg")}</th><th>{t("kg_per_picker_hour")}</th><th>{t("kg_per_picker")}</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} data-testid={`${testid}-row`}><td className="font-semibold min-w-[150px]">{r.name}{r.missing_hours && <span className="block text-xs font-bold text-amber-800">{t("missing_hours")}</span>}</td>
          <td className="font-mono">{r.pickers}</td><td className="font-mono">{r.picker_hours}</td><td className="font-mono">{r.verified_kg}</td>
          <td className="font-mono font-bold text-lg">{r.kg_per_picker_hour ?? "—"}</td><td className="font-mono">{r.kg_per_picker ?? "—"}</td></tr>))}
        {!rows.length && <tr><td colSpan={6} className="text-slate-500">{t("none")}</td></tr>}</tbody></table>
  </div>
);

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [q, setQ] = useState({ date: todayCL(), grower_id: "", farm_id: "" });
  const [d, setD] = useState(null);
  const [pending, setPending] = useState([]);
  useEffect(() => { if (user?.role === "packing_house") return; api.get("/dispatch/pending", { params: q.farm_id ? { farm_id: q.farm_id } : {} }).then((r) => setPending(r.data)).catch(() => {}); }, [q.farm_id]);
  useEffect(() => {
    api.get("/dashboard", { params: Object.fromEntries(Object.entries(q).filter(([, v]) => v)) }).then((r) => setD(r.data)).catch(() => {});
  }, [q]);
  if (!d) return <p>{t("loading")}</p>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {user?.role === "admin" && <SetupChecklist />}
      <PageTitle>{t("dashboard_title")}</PageTitle>
      <ScopeFilters q={q} setQ={setQ} />
      <PendingList rows={pending} compact />
      <div className="space-y-2"><p className="eyebrow">{t("today")} · {d.date}</p><KpiRow d={d.today} target={d.target_kg} t={t} prefix="today" /></div>
      <div className="space-y-2"><p className="eyebrow">{t("season")} · {d.season?.name}</p><KpiRow d={d.season_totals} target={d.target_kg} t={t} prefix="season" /></div>
      <div className="space-y-2"><p className="eyebrow">{t("productivity")} · {d.date}</p>
        <div className="grid lg:grid-cols-2 gap-4">
          <Productivity title={t("by_contractor")} rows={d.productivity_contractor} t={t} testid="productivity-contractor" />
          <Productivity title={t("by_crew")} rows={d.productivity_crew} t={t} testid="productivity-crew" />
        </div></div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid md:grid-cols-2 gap-4">
          <Breakdown title={t("by_contractor")} rows={d.by_contractor} t={t} testid="breakdown-contractor" />
          <Breakdown title={t("by_sector")} rows={d.by_sector} t={t} testid="breakdown-sector" />
          <Breakdown title={t("by_variety")} rows={d.by_variety} t={t} testid="breakdown-variety" />
          <Breakdown title={t("by_farm")} rows={d.by_farm} t={t} testid="breakdown-farm" />
        </div>
        <div className="space-y-4">
          <Kpi label={t("headcount")} value={`${d.headcount.actual} / ${d.headcount.planned}`} sub={`${t("actual")} / ${t("planned")}`} testid="kpi-headcount" />
          <div className="card" data-testid="exceptions-list">
            <h3 className="font-bold px-4 py-3 border-b flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" />{t("exceptions")} ({d.exceptions.length})</h3>
            <ul className="max-h-[480px] overflow-y-auto">
              {d.exceptions.map((e, i) => (
                <li key={i} className="px-4 py-2 border-b border-slate-100 text-sm">
                  <Link to={`/bin/${e.bin_id}`} className="font-mono font-bold text-[#800B28] underline">{e.label}</Link>
                  <span className="ml-2 font-bold">{t(`ex_${e.type}`)}</span>
                  {e.detail && <span className="block text-slate-600">{e.detail}</span>}
                </li>
              ))}
              {!d.exceptions.length && <li className="px-4 py-3 text-slate-500">{t("none")}</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
